import { useState } from 'react';
import type { WorkflowStatus } from '@pm-next/shared-types';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, CheckSquare, Workflow } from 'lucide-react';
import { WorkflowDesigner } from './designer/WorkflowDesigner';
import { WorkflowHomeView, type WorkflowListItem } from './WorkflowHomeView';
import { WorkflowNotificationsList, WorkflowTasksList } from './WorkflowActivityView';
import {
  useWorkflows,
  useCreateWorkflow,
  useDeleteWorkflow,
  useDuplicateWorkflow,
  useCreateFromTemplate,
  useGenerateWorkflow,
} from '@/hooks/useWorkflows';

type View = 'home' | 'designer';

const VALID_STATUSES: WorkflowStatus[] = ['draft', 'active', 'inactive', 'archived'];

function mapApiWorkflowToListItem(w: {
  id: string;
  name: string;
  status: string;
  description: string | null;
}): WorkflowListItem {
  return {
    id: w.id,
    name: w.name,
    status: (VALID_STATUSES.includes(w.status as WorkflowStatus)
      ? w.status
      : 'draft') as WorkflowStatus,
    description: w.description ?? undefined,
  };
}

export function WorkflowsView() {
  const { toast } = useToast();
  const [view, setView] = useState<View>('home');
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: workflowsResponse, isLoading } = useWorkflows({
    limit: 100,
    search: search.trim() || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });
  const workflows: WorkflowListItem[] = (workflowsResponse?.data ?? []).map(
    mapApiWorkflowToListItem
  );

  const createMutation = useCreateWorkflow();
  const deleteMutation = useDeleteWorkflow();
  const duplicateMutation = useDuplicateWorkflow();
  const createFromTemplateMutation = useCreateFromTemplate();
  const generateMutation = useGenerateWorkflow();

  const openDesigner = (opts: { workflowId?: string | null; templateId?: string | null }) => {
    setWorkflowId(opts.workflowId ?? null);
    setTemplateId(opts.templateId ?? null);
    setView('designer');
  };

  const goHome = () => {
    setView('home');
    setWorkflowId(null);
    setTemplateId(null);
  };

  const handleCreateWorkflow = async () => {
    try {
      const created = await createMutation.mutateAsync({
        name: 'Untitled workflow',
      });
      openDesigner({ workflowId: created.id });
      toast({ title: 'Workflow created', description: 'You can now add steps.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to create workflow',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleDeleteWorkflow = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast({ title: 'Workflow deleted' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete workflow',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleDuplicateWorkflow = async (id: string) => {
    try {
      await duplicateMutation.mutateAsync(id);
      toast({ title: 'Workflow duplicated', description: 'A copy was created as a draft.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to duplicate workflow',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleUseTemplate = async (templateId: string) => {
    try {
      const created = await createFromTemplateMutation.mutateAsync(templateId);
      openDesigner({ workflowId: created.id });
      toast({
        title: 'Workflow created',
        description: `Created from template. You can now edit and save.`,
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to create from template',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleGenerateWorkflow = async (description: string) => {
    try {
      const created = await generateMutation.mutateAsync(description);
      openDesigner({ workflowId: created.id });
      toast({
        title: 'Workflow generated',
        description: 'Review the generated steps, then save when ready.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to generate workflow',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  if (view === 'designer') {
    return (
      <div className="flex h-full flex-col">
        <WorkflowDesigner
          workflowId={workflowId}
          templateId={templateId ?? undefined}
          onBack={goHome}
        />
      </div>
    );
  }

  return (
    <Tabs defaultValue="workflows" className="flex h-full flex-col overflow-hidden">
      <div className="border-b px-3">
        <TabsList className="h-9 w-full justify-start gap-1 bg-transparent p-0">
          <TabsTrigger
            value="workflows"
            className="h-8 rounded-none border-b-2 border-transparent px-3 data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            <Workflow className="mr-1.5 h-3.5 w-3.5" />
            Workflows
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className="h-8 rounded-none border-b-2 border-transparent px-3 data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            <Bell className="mr-1.5 h-3.5 w-3.5" />
            Notifications
          </TabsTrigger>
          <TabsTrigger
            value="tasks"
            className="h-8 rounded-none border-b-2 border-transparent px-3 data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            <CheckSquare className="mr-1.5 h-3.5 w-3.5" />
            Tasks
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="workflows" className="mt-0 flex min-h-0 flex-1 flex-col">
        <WorkflowHomeView
          workflows={workflows}
          isLoading={isLoading}
          search={search}
          statusFilter={statusFilter}
          onSearchChange={setSearch}
          onStatusFilterChange={setStatusFilter}
          onOpenWorkflow={(id) => openDesigner({ workflowId: id })}
          onCreateWorkflow={handleCreateWorkflow}
          onUseTemplate={handleUseTemplate}
          onDeleteWorkflow={handleDeleteWorkflow}
          onDuplicateWorkflow={handleDuplicateWorkflow}
          onGenerateWorkflow={handleGenerateWorkflow}
          isGenerating={generateMutation.isPending}
        />
      </TabsContent>
      <TabsContent value="notifications" className="mt-0 min-h-0 flex-1">
        <WorkflowNotificationsList />
      </TabsContent>
      <TabsContent value="tasks" className="mt-0 min-h-0 flex-1">
        <WorkflowTasksList />
      </TabsContent>
    </Tabs>
  );
}
